import {IssueFieldSingleSelectValueToken} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {Link} from '@primer/react'
import {useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import type {IssueEventRationales$data} from './__generated__/IssueEventRationales.graphql'
import type {IssueFieldAddedEvent$key} from './__generated__/IssueFieldAddedEvent.graphql'
import type {IssueFieldChangedEvent$key} from './__generated__/IssueFieldChangedEvent.graphql'
import type {IssueFieldRemovedEvent$key} from './__generated__/IssueFieldRemovedEvent.graphql'
import {IssueEventRationale} from './IssueEventRationale'
import {IssueFieldAddedEventFragment} from './IssueFieldAddedEvent'
import {IssueFieldChangedEventFragment} from './IssueFieldChangedEvent'
import {getSearchLink} from './IssueFieldEvent'
import styles from './IssueFieldEvent.module.css'
import {IssueFieldRemovedEventFragment} from './IssueFieldRemovedEvent'

type IssueFieldEventKey = IssueFieldAddedEvent$key | IssueFieldChangedEvent$key | IssueFieldRemovedEvent$key

type Rationales = Record<string, IssueEventRationales$data['eventRationales'][number]>

type RolledupIssueFieldEventProps = {
  rollupGroup: Record<string, IssueFieldEventKey[]>
  repositoryNameWithOwner?: string
  rationales?: Rationales
}

/**
 * Renders a bundled summary of multiple issue field changes.
 *
 * Example output: "updated Product pillar Copilot Intelligence Platform, Trending At risk and Deliverable Staffship/Internal Release, and also removed Eng Staffing"
 */
export function RolledupIssueFieldEvent({
  rollupGroup,
  repositoryNameWithOwner,
  rationales,
}: RolledupIssueFieldEventProps): React.ReactElement | null {
  const addedEvents = (rollupGroup['IssueFieldAddedEvent'] as IssueFieldAddedEvent$key[]) || []
  const changedEvents = (rollupGroup['IssueFieldChangedEvent'] as IssueFieldChangedEvent$key[]) || []
  const removedEvents = (rollupGroup['IssueFieldRemovedEvent'] as IssueFieldRemovedEvent$key[]) || []

  const hasUpdated = addedEvents.length > 0 || changedEvents.length > 0
  const hasRemoved = removedEvents.length > 0
  const hasContent = hasUpdated || hasRemoved

  if (!hasContent) {
    return null
  }

  return (
    <>
      {hasUpdated && (
        <>
          {`${LABELS.timeline.issueFieldRollup.updated} `}
          <UpdatedFieldsList
            addedEvents={addedEvents}
            changedEvents={changedEvents}
            repositoryNameWithOwner={repositoryNameWithOwner}
            rationales={rationales}
          />
        </>
      )}
      {hasUpdated && hasRemoved && `, ${LABELS.timeline.issueFieldRollup.andAlso} `}
      {hasRemoved && (
        <>
          {`${LABELS.timeline.issueFieldRollup.removed} `}
          <RemovedFieldsList removedEvents={removedEvents} rationales={rationales} />
        </>
      )}{' '}
    </>
  )
}

type UpdatedFieldsListProps = {
  addedEvents: IssueFieldAddedEvent$key[]
  changedEvents: IssueFieldChangedEvent$key[]
  repositoryNameWithOwner?: string
  rationales?: Rationales
}

function UpdatedFieldsList({addedEvents, changedEvents, repositoryNameWithOwner, rationales}: UpdatedFieldsListProps) {
  const totalCount = addedEvents.length + changedEvents.length

  return (
    <>
      {addedEvents.map((eventKey, index) => {
        const isLastOverall = index === totalCount - 1
        const isSecondToLastOverall = index === totalCount - 2
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <AddedFieldItem
            key={`added-${eventIndex}`}
            eventKey={eventKey}
            repositoryNameWithOwner={repositoryNameWithOwner}
            isLast={isLastOverall}
            isSecondToLast={isSecondToLastOverall}
            rationales={rationales}
          />
        )
      })}
      {changedEvents.map((eventKey, index) => {
        const globalIndex = addedEvents.length + index
        const isLastOverall = globalIndex === totalCount - 1
        const isSecondToLastOverall = globalIndex === totalCount - 2
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <ChangedFieldItem
            key={`changed-${eventIndex}`}
            eventKey={eventKey}
            repositoryNameWithOwner={repositoryNameWithOwner}
            isLast={isLastOverall}
            isSecondToLast={isSecondToLastOverall}
            rationales={rationales}
          />
        )
      })}
    </>
  )
}

type RemovedFieldsListProps = {
  removedEvents: IssueFieldRemovedEvent$key[]
  rationales?: Rationales
}

function RemovedFieldsList({removedEvents, rationales}: RemovedFieldsListProps) {
  return (
    <>
      {removedEvents.map((eventKey, index) => {
        const eventIndex = (eventKey as {__index?: number}).__index ?? index
        return (
          <RemovedFieldItem
            key={`removed-${eventIndex}`}
            eventKey={eventKey}
            isLast={index === removedEvents.length - 1}
            isSecondToLast={index === removedEvents.length - 2}
            rationales={rationales}
          />
        )
      })}
    </>
  )
}

type AddedFieldItemProps = {
  eventKey: IssueFieldAddedEvent$key
  repositoryNameWithOwner?: string
  isLast: boolean
  isSecondToLast: boolean
  rationales?: Rationales
}

function AddedFieldItem({eventKey, repositoryNameWithOwner, isLast, isSecondToLast, rationales}: AddedFieldItemProps) {
  const data = useFragment(IssueFieldAddedEventFragment, eventKey)

  if (!data.issueField?.name || !data.issueField?.dataType) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '
  const searchLink = getSearchLink(repositoryNameWithOwner, data.issueField.name, data.value ?? undefined)
  const isSingleSelect = data.issueField.dataType === 'SINGLE_SELECT'
  const rationale = rationales?.[data.id]

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {data.value && (
        <span>
          {' '}
          {isSingleSelect ? (
            <Link href={searchLink} className={styles.inlineToken}>
              <IssueFieldSingleSelectValueToken
                name={data.value}
                color={data.color || ''}
                getTooltipText={() => undefined}
              />
            </Link>
          ) : (
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {data.value}
            </Link>
          )}
        </span>
      )}
      {rationale?.rationale && (
        <IssueEventRationale
          rationale={rationale.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_ADDED"
        />
      )}
      {separator}
    </span>
  )
}

type ChangedFieldItemProps = {
  eventKey: IssueFieldChangedEvent$key
  repositoryNameWithOwner?: string
  isLast: boolean
  isSecondToLast: boolean
  rationales?: Rationales
}

function ChangedFieldItem({
  eventKey,
  repositoryNameWithOwner,
  isLast,
  isSecondToLast,
  rationales,
}: ChangedFieldItemProps) {
  const data = useFragment(IssueFieldChangedEventFragment, eventKey)

  if (!data.issueField?.name || !data.issueField?.dataType) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '
  const searchLink = getSearchLink(repositoryNameWithOwner, data.issueField.name, data.newValue ?? undefined)
  const isSingleSelect = data.issueField.dataType === 'SINGLE_SELECT'
  const rationale = rationales?.[data.id]

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {data.newValue && (
        <span>
          {' '}
          {isSingleSelect ? (
            <Link href={searchLink} className={styles.inlineToken}>
              <IssueFieldSingleSelectValueToken
                name={data.newValue}
                color={data.newColor || ''}
                getTooltipText={() => undefined}
              />
            </Link>
          ) : (
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {data.newValue}
            </Link>
          )}
        </span>
      )}
      {rationale?.rationale && (
        <IssueEventRationale
          rationale={rationale.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_CHANGED"
        />
      )}
      {separator}
    </span>
  )
}

type RemovedFieldItemProps = {
  eventKey: IssueFieldRemovedEvent$key
  isLast: boolean
  isSecondToLast: boolean
  rationales?: Rationales
}

function RemovedFieldItem({eventKey, isLast, isSecondToLast, rationales}: RemovedFieldItemProps) {
  const data = useFragment(IssueFieldRemovedEventFragment, eventKey)

  if (!data.issueField?.name) return null

  const separator = isLast ? '' : isSecondToLast ? ` ${LABELS.timeline.and} ` : ', '
  const rationale = rationales?.[data.id]

  return (
    <span className={styles.issueFieldRollupValueWrapper}>
      <span className={styles.rollupFieldName}>{data.issueField.name}</span>
      {rationale?.rationale && (
        <IssueEventRationale
          rationale={rationale.rationale}
          issueEventDatabaseId={String(data.databaseId)}
          analyticsAction="ISSUE_FIELD_REMOVED"
        />
      )}
      {separator}
    </span>
  )
}
